/**
 * Gift card actions ported from reware-admin's Gift cards portal
 * (giftcards-api.js). The portal is read-only apart from resend-email and the
 * audited reveal of number/PIN — the only two writes that file exposes.
 */
import { execAdmin } from '@src/apis/reware/utils/exec';

import { revealSensitiveDetail } from './fetchGiftcards';

interface ResendGiftcardEmailResponse {
  giftcard: { resendGiftcardEmail: { id: number } | null } | null;
}

/**
 * The response has to be checked, not just awaited. `execAdmin` only throws when the payload
 * carries `errors[0].message`, and the mutation root resolves `null` for an id the caller is not
 * authorized for — no error. Awaiting and discarding would report a green "email resent" for an
 * email that was never sent.
 */
export const resendGiftcardEmail = async (giftcardId: number): Promise<void> => {
  const data = await execAdmin<{ giftcardId: number }, ResendGiftcardEmailResponse>(
    `mutation resendGiftcardEmail($giftcardId: Int!) {
      giftcard(id: $giftcardId) { resendGiftcardEmail { id } }
    }`,
    { variables: { giftcardId } }
  );

  if (!data?.giftcard?.resendGiftcardEmail) {
    throw new Error(`Gift card ${giftcardId} did not accept the resend — no email was sent`);
  }
};

/** Reveal the full number or PIN (server-audited). */
export const reveal = async (giftcardId: number, field: 'number' | 'pin'): Promise<string | null> =>
  revealSensitiveDetail(giftcardId, field);

